import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Section, Eyebrow } from "@/components/PublicLayout";
import { ArrowRight, Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

function List({ title, items }) {
  if (!items || !items.length) return null;
  return (
    <div className="mb-8">
      <div className="eyebrow text-ink/40 mb-3">{title}</div>
      <ul className="space-y-2">{items.map((it, i) => (
        <li key={i} className="flex gap-2.5 text-ink/75"><Check className="h-4 w-4 text-heraldic mt-1 shrink-0" /><span>{it}</span></li>
      ))}</ul>
    </div>
  );
}

export default function ProgrammeDetail() {
  const { slug } = useParams();
  const { t, field, lang } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/programmes/${slug}`).then((r) => setData(r.data)).catch(() => setData(null)); }, [slug]);
  if (!data) return <Section className="py-24"><p className="text-ink/50">{t("loading")}</p></Section>;
  const p = data.programme, tp = data.tuition;
  const method = data.method, competencies = data.competencies;
  const loc = (base) => (p[`${base}_${lang}`] ?? p[base]);
  const sv = (obj, key) => (obj?.[`${key}_${lang}`] ?? obj?.[`${key}_en`]);
  const structure = p[`structure`] || [];
  return (
    <div>
      <div className="bg-ink text-paper">
        <Section className="py-20 md:py-28">
          <Eyebrow>{p.level} · {p.duration} · {p.format}</Eyebrow>
          <h1 className="font-serif text-4xl md:text-6xl tracking-tight max-w-4xl">{field(p, "name")}</h1>
          <p className="mt-5 max-w-2xl text-paper/70 text-lg leading-relaxed">{field(p, "purpose")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {(p.languages || []).map((l) => <span key={l} className="border border-white/20 px-3 py-1 rounded-sm text-xs">{l}</span>)}
          </div>
        </Section>
      </div>

      {/* Overview */}
      {loc("overview") && (
        <Section className="py-14 md:py-20 border-b border-border">
          <div className="eyebrow text-heraldic mb-3">{t("pd_overview")}</div>
          <p data-testid="pd-overview" className="font-serif text-2xl md:text-3xl leading-snug text-ink max-w-4xl">{loc("overview")}</p>
        </Section>
      )}

      {/* The Simo learning cycle */}
      {method && (
        <Section className="py-14 md:py-20 bg-secondary/40 border-b border-border">
          <div className="eyebrow text-ink/40 mb-2">{sv(method, "eyebrow")}</div>
          <h2 className="font-serif text-2xl md:text-3xl text-ink max-w-3xl">{sv(method, "title")}</h2>
          <p className="mt-4 text-ink/70 max-w-2xl leading-relaxed">{sv(method, "intro")}</p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {method.steps.map((s) => (
              <div key={s.n} data-testid={`method-step-${s.n}`} className="border-t-2 border-heraldic pt-4">
                <div className="font-mono text-xs text-heraldic mb-2">{s.n}</div>
                <div className="font-serif text-xl text-ink">{sv(s, "title")}</div>
                <p className="mt-2 text-sm text-ink/65 leading-relaxed">{sv(s, "desc")}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section className="py-16 md:py-24 grid lg:grid-cols-3 gap-14">
        <div className="lg:col-span-2">
          {/* Structured path */}
          {structure.length > 0 && (
            <div className="mb-12">
              <div className="eyebrow text-ink/40 mb-4">{t("pd_structure")}</div>
              <div className="space-y-6">
                {structure.map((b, i) => (
                  <div key={i} data-testid={`structure-block-${i}`} className="border border-border rounded-sm p-6 bg-paper">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div className="font-serif text-xl text-ink">{sv(b, "title")}</div>
                      <div className="text-xs text-heraldic">{t("pd_focus")}: {sv(b, "focus")}</div>
                    </div>
                    <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                      {(b[`points_${lang}`] || b.points_en || []).map((pt, j) => (
                        <li key={j} className="flex gap-2.5 text-sm text-ink/75"><Check className="h-4 w-4 text-heraldic mt-0.5 shrink-0" /><span>{pt}</span></li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Faculties of Mind */}
          {competencies && (
            <div className="mb-12 border-l-2 border-heraldic pl-6">
              <div className="eyebrow text-heraldic mb-2">{sv(competencies, "eyebrow")}</div>
              <h3 className="font-serif text-xl text-ink">{sv(competencies, "title")}</h3>
              <p className="mt-2 text-sm text-ink/65 leading-relaxed max-w-2xl">{sv(competencies, "intro")}</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {competencies.items.map((c, i) => (
                  <div key={i} data-testid={`faculty-${i}`}>
                    <div className="text-sm font-medium text-ink">{sv(c, "name")}</div>
                    <p className="text-xs text-ink/60 leading-relaxed mt-0.5">{sv(c, "desc")}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <List title={t("pd_curriculum")} items={loc("curriculum")} />
          <List title={t("pd_outcomes")} items={loc("learning_outcomes")} />

          {/* Capstone */}
          {loc("capstone") && (
            <div className="mb-10 bg-ink text-paper rounded-sm p-7">
              <div className="eyebrow text-paper/50 mb-2">{t("pd_capstone")}</div>
              <p data-testid="pd-capstone" className="text-paper/90 leading-relaxed font-serif text-lg">{loc("capstone")}</p>
            </div>
          )}

          {loc("career_prep") && <div className="mb-8"><div className="eyebrow text-ink/40 mb-3">{t("pd_career")}</div><p className="text-ink/75 leading-relaxed">{loc("career_prep")}</p></div>}
          {loc("entry_pathway") && <div className="mb-8"><div className="eyebrow text-ink/40 mb-3">{t("pd_entry")}</div><p className="text-ink/75 leading-relaxed">{loc("entry_pathway")}</p></div>}
          {loc("credential") && <div className="mb-8 border-l-2 border-heraldic pl-5"><div className="eyebrow text-heraldic mb-2">{t("pd_credential")}</div><p className="text-ink/75 leading-relaxed">{loc("credential")}</p></div>}

          {/* Africa-specific conversion note */}
          {loc("africa") && (
            <div data-testid="pd-africa" className="mb-8 rounded-sm p-6 bg-heraldic/8 border border-heraldic/20">
              <div className="eyebrow text-heraldic mb-2">{t("pd_africa")}</div>
              <p className="text-ink/80 leading-relaxed">{loc("africa")}</p>
            </div>
          )}

          <List title={t("pd_disclosures")} items={loc("disclosures")} />
          {p.faqs && p.faqs.length > 0 && (
            <div className="mt-8"><div className="eyebrow text-ink/40 mb-3">{t("pd_faq")}</div>
              <Accordion type="single" collapsible>
                {p.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`}><AccordionTrigger className="text-left font-serif text-lg">{f[`q_${lang}`] || f.q_en}</AccordionTrigger><AccordionContent className="text-ink/70">{f[`a_${lang}`] || f.a_en}</AccordionContent></AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>
        <aside className="lg:col-span-1">
          <div className="border border-border rounded-sm p-6 sticky top-24 bg-secondary/40">
            <div className="eyebrow text-ink/40 mb-3">{t("pd_tuition")}</div>
            {tp && tp.net != null ? (
              <div>
                <div className="font-serif text-4xl text-ink">€{tp.net.toLocaleString()}<span className="text-base text-ink/50"> {t("per_year_short")}</span></div>
                <div className="mt-1 text-sm text-ink/55 line-through">€{tp.institutional?.toLocaleString()} {t("pd_institutional")}</div>
                <div className="mt-2 inline-block bg-heraldic/10 text-heraldic text-xs px-2.5 py-1 rounded-sm">{t("pd_grant")} −{tp.grant_pct}%</div>
                <p className="mt-3 text-xs text-ink/50">{t("pd_indicative")}</p>
              </div>
            ) : (
              <div className="font-serif text-2xl text-ink">{tp?.note || t("pd_tbc")}</div>
            )}
            <div className="mt-4 pt-4 border-t border-border text-sm text-ink/70">{t("app_fee")} <span className="float-right font-mono">€97</span></div>
            <Link to="/apply" data-testid="prog-apply" className="mt-6 flex items-center justify-center gap-2 bg-heraldic text-white px-5 py-3 rounded-sm hover:bg-heraldic-700 transition-colors">{t("pd_apply")} <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/tuition" className="mt-3 block text-center text-sm text-ink/60 hover:text-ink">{t("pd_view_engine")}</Link>
          </div>
        </aside>
      </Section>
    </div>
  );
}
